import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ShieldCheck } from 'lucide-react';

interface ParentalConsentGateProps {
  onConsentComplete: () => void;
}

export function ParentalConsentGate({ onConsentComplete }: ParentalConsentGateProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValid = parentName.trim().length >= 2 && parentEmail.includes('@') && agreedToTerms;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !user) return;

    setIsSubmitting(true);
    try {
      const now = new Date().toISOString();

      await supabase
        .from('profiles')
        .update({
          parent_name: parentName.trim(),
          parent_email: parentEmail.trim().toLowerCase(),
          parental_consent_at: now,
        } as any)
        .eq('user_id', user.id);

      await supabase.from('consent_records').insert({
        user_id: user.id,
        consent_type: 'parental_consent',
        consent_version: '2026-03-01',
        granted_at: now,
        metadata: {
          parent_name: parentName.trim(),
          parent_email: parentEmail.trim().toLowerCase(),
        },
      } as any);

      toast({
        title: 'Parental Consent Recorded',
        description: 'Thank you! The account is now activated.',
      });
      onConsentComplete();
    } catch {
      toast({
        title: 'Error',
        description: 'Could not save consent. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex items-start justify-center overflow-y-auto">
      <div className="w-full max-w-lg mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="mx-auto w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
            <ShieldCheck className="w-7 h-7 text-emerald-700" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Parental Consent Required</h1>
          <p className="text-gray-600 text-sm">
            Because this account belongs to a user under 18, a parent or legal guardian must
            provide consent before the account can be used.
          </p>
        </div>

        <div className="bg-gray-50 rounded-xl p-5 mb-6 space-y-3">
          <h3 className="font-semibold text-gray-900 text-sm">What you're consenting to:</h3>
          <ul className="text-sm text-gray-700 space-y-2">
            <li className="flex gap-2">
              <span className="text-emerald-600 flex-shrink-0">•</span>
              Your child will engage in golf-related physical activity guided by the app
            </li>
            <li className="flex gap-2">
              <span className="text-emerald-600 flex-shrink-0">•</span>
              AI technology will analyze uploaded swing videos to provide feedback
            </li>
            <li className="flex gap-2">
              <span className="text-emerald-600 flex-shrink-0">•</span>
              Video uploads may be processed for body position and movement patterns
            </li>
            <li className="flex gap-2">
              <span className="text-emerald-600 flex-shrink-0">•</span>
              You accept the Terms of Service and Privacy Policy on behalf of the minor
            </li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1.5">
              Parent/Guardian Full Name
            </label>
            <Input
              value={parentName}
              onChange={(e) => setParentName(e.target.value)}
              placeholder="Jane Smith"
              className="h-12"
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1.5">
              Parent/Guardian Email
            </label>
            <Input
              type="email"
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
              placeholder="parent@example.com"
              className="h-12"
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-4">
            <Checkbox
              checked={agreedToTerms}
              onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
              disabled={isSubmitting}
              className="mt-0.5 flex-shrink-0"
            />
            <div className="text-xs text-gray-600 leading-relaxed">
              I am the parent or legal guardian of this user. I have read and agree to the{' '}
              <Link to="/terms" target="_blank" className="text-emerald-700 hover:underline">
                Terms of Service
              </Link>
              ,{' '}
              <Link to="/privacy" target="_blank" className="text-emerald-700 hover:underline">
                Privacy Policy
              </Link>
              ,{' '}
              <Link to="/terms/ai-disclosure" target="_blank" className="text-emerald-700 hover:underline">
                AI Disclosure Addendum
              </Link>
              , and{' '}
              <Link to="/terms/parental-consent" target="_blank" className="text-emerald-700 hover:underline">
                Parental Consent Addendum
              </Link>{' '}
              on behalf of this minor. I accept full responsibility for their use of the Service.
            </div>
          </div>

          <Button
            type="submit"
            disabled={!isValid || isSubmitting}
            className="w-full h-12 bg-emerald-700 hover:bg-emerald-800 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Confirm Parental Consent'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
